/**
 * Kopstorm, 22 Oct 2026. Product card colour swatches.
 *
 * A swatch on a product card swaps the card to that colour in place: main image, swipe gallery
 * (mc-collection-images), card links, quick buy and, on AW26 cards, the price when the colour has its own price.
 * The images of every colour are preloaded once the card is close to the viewport, so the swap is instant.
 *
 * Data comes from script[data-kop-swatch-data] inside the swatch row, keyed by variant id:
 *   snippets/aw26-card-swatches.liquid          AW26 cards, swatches are links (a.color-swatch)
 *   snippets/product-card-color-swatches.liquid theme cards, swatches are radios + labels
 * Without data a swatch keeps its old behaviour (link to the PDP, or the theme's own product-card script).
 */
(function () {
  if (window.kopCardSwatches) return;
  window.kopCardSwatches = true;

  var WIDTHS = [200, 400, 600, 800, 1000, 1200];
  var ROW = '.aw26-card-swatches, .product-card__swatch-list';

  function sizedUrl(src, width) {
    return /[?&]width=\d+/.test(src)
      ? src.replace(/([?&])width=\d+/, '$1width=' + width)
      : src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function srcset(media) {
    return WIDTHS
      .filter(function (w) { return !media.w || w <= media.w; })
      .map(function (w) { return sizedUrl(media.src, w) + ' ' + w + 'w'; })
      .join(', ');
  }

  function rowData(row) {
    if (row._kopSwatchData !== undefined) return row._kopSwatchData;
    var script = row.querySelector('script[data-kop-swatch-data]');
    try {
      row._kopSwatchData = script ? JSON.parse(script.textContent) : null;
    } catch (err) {
      row._kopSwatchData = null;
    }
    return row._kopSwatchData;
  }

  function cardOf(row) {
    return row.closest('product-card') || row.closest('.product-card');
  }

  function primaryOf(card) {
    return card && card.querySelector('.product-card__figure .product-card__image--primary');
  }

  // Same attributes as the image the swap inserts, so the browser picks the same file and serves it from cache.
  function buildImage(media, attrs) {
    var img = document.createElement('img');
    img.className = attrs.className || '';
    img.alt = attrs.alt || '';
    img.loading = attrs.loading || 'lazy';
    if (attrs.sizes) img.sizes = attrs.sizes;
    if (media.w) img.width = media.w;
    if (media.h) img.height = media.h;
    img.srcset = srcset(media);
    img.src = sizedUrl(media.src, 1000);
    return img;
  }

  /* ── preload ─────────────────────────────────────────────────────────── */

  function preloadRow(row) {
    if (row._kopPreloaded) return;
    row._kopPreloaded = true;
    var data = rowData(row);
    var primary = primaryOf(cardOf(row));
    if (!data || !primary) return;
    var sizes = primary.getAttribute('sizes');
    var keep = row._kopPreloadImages = [];
    Object.keys(data).forEach(function (id) {
      var entry = data[id];
      if (!entry.image || !entry.image.src) return;
      var img = new Image();
      if (sizes) img.sizes = sizes;
      img.fetchPriority = 'low';
      img.srcset = srcset(entry.image);
      img.src = sizedUrl(entry.image.src, 1000);
      keep.push(img);
    });
  }

  var saveData = navigator.connection && navigator.connection.saveData;
  var observer = !saveData && 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          observer.unobserve(e.target);
          preloadRow(e.target);
        });
      }, { rootMargin: '200px 0px' })
    : null;

  function observeRows(root) {
    if (!observer) return;
    (root || document).querySelectorAll('script[data-kop-swatch-data]').forEach(function (script) {
      var row = script.closest(ROW);
      if (!row || row._kopObserved) return;
      row._kopObserved = true;
      observer.observe(row);
    });
  }

  // Cards added later (load more, filters, carousels) are picked up as they arrive.
  if (observer) {
    var scanQueued = false;
    new MutationObserver(function (records) {
      if (scanQueued) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          scanQueued = true;
          requestAnimationFrame(function () { scanQueued = false; observeRows(); });
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // First touch or hover on a row preloads straight away, also when the observer is not available.
  ['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var row = e.target && e.target.closest && e.target.closest(ROW);
      if (row && rowData(row)) preloadRow(row);
    }, { capture: true, passive: true });
  });

  /* ── swap ────────────────────────────────────────────────────────────── */

  function productHandle(card) {
    var handle = card.getAttribute('handle');
    if (handle) return handle;
    var link = card.querySelector('.product-card__title a[href*="/products/"]');
    var match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  // Only links to this product get the new variant; links to other products on the card stay untouched.
  function variantHref(href, handle, variantId) {
    try {
      var url = new URL(href, window.location.origin);
      var match = url.pathname.match(/\/products\/([^/?#]+)\/?$/);
      if (!match || (handle && match[1] !== handle)) return null;
      url.searchParams.set('variant', variantId);
      return url.pathname + url.search + url.hash;
    } catch (err) {
      return null;
    }
  }

  function updatePrice(card, row, entry) {
    if (entry.price === undefined) return;
    var currentPrice = Number(row.getAttribute('data-current-price'));
    var currentCompare = Number(row.getAttribute('data-current-compare'));
    var nextCompare = Number(entry.compare) || 0;
    row.setAttribute('data-current-price', entry.price);
    row.setAttribute('data-current-compare', nextCompare);
    // Same price for this colour: leave the price block alone, so a running Elevate price test keeps its values.
    if (currentPrice === Number(entry.price) && currentCompare === nextCompare) return;

    var pricing = card.querySelector('.card-pricing');
    if (!pricing) return;
    var onSale = nextCompare > Number(entry.price);
    var wrap = pricing.querySelector('.card_wrap');
    var sale = pricing.querySelector('[data-eab-sale-price]');
    var compare = pricing.querySelector('.card_compare');
    var savings = pricing.querySelector('.card_savings');

    pricing.setAttribute('data-variant-id', entry.id);
    if (sale) {
      sale.textContent = entry.priceText;
      if (onSale) sale.style.setProperty('color', '#FF6647', 'important');
      else sale.style.removeProperty('color');
    }
    if (onSale) {
      if (!compare && wrap) {
        compare = document.createElement('span');
        compare.className = 'card_compare';
        wrap.insertBefore(compare, wrap.firstChild);
      }
      if (compare) compare.textContent = entry.compareText;
      if (!savings) {
        savings = document.createElement('span');
        savings.className = 'card_savings';
        pricing.appendChild(savings);
      }
      savings.textContent = entry.saveText;
    } else {
      if (compare) compare.remove();
      if (savings) savings.remove();
    }
  }

  function applySwatch(row, entry) {
    var card = cardOf(row);
    if (!card) return;
    var handle = productHandle(card);
    var figure = card.querySelector('.product-card__figure');
    var primary = primaryOf(card);
    var token = (row._kopSwatchToken || 0) + 1;
    row._kopSwatchToken = token;

    card.querySelectorAll('a[href]').forEach(function (link) {
      if (link.closest(ROW)) return;
      var next = variantHref(link.getAttribute('href'), handle, entry.id);
      if (next) link.setAttribute('href', next);
    });

    // Quick buy opens on this colour; a drawer that was opened before loads again with the new colour.
    var drawer = card.querySelector('quick-buy-drawer');
    if (drawer) {
      var drawerHandle = (drawer.getAttribute('handle') || '').split('?')[0];
      if (drawerHandle) {
        drawer.setAttribute('handle', drawerHandle + '?variant=' + entry.id);
        if (!drawer.hasAttribute('open')) drawer._hasLoaded = false;
      }
    }

    updatePrice(card, row, entry);

    if (!primary || !entry.image || !entry.image.src) return;

    var sizes = primary.getAttribute('sizes');
    var next = buildImage(entry.image, { className: primary.className, sizes: sizes, alt: primary.alt, loading: 'eager' });
    var secondary = figure.querySelector('.product-card__image--secondary');
    var ready = next.decode ? next.decode().catch(function () {}) : Promise.resolve();

    ready.then(function () {
      if (row._kopSwatchToken !== token) return;
      primary.replaceWith(next);

      if (secondary && entry.second && entry.second.src) {
        secondary.replaceWith(buildImage(entry.second, { className: secondary.className, sizes: secondary.getAttribute('sizes') || sizes, alt: secondary.alt }));
      }

      var gallery = figure.querySelector('mc-collection-images');
      if (!gallery) return;

      // A fresh gallery element with the images of this colour starts at slide one.
      var lead = next.closest('a');
      if (!lead || !gallery.contains(lead)) return;
      var label = (gallery.getAttribute('aria-label') || '').split(' — ')[0];
      var fresh = document.createElement('mc-collection-images');
      fresh.setAttribute('aria-label', label + ' — ' + entry.color);
      var leadClone = lead.cloneNode(true);
      leadClone.removeAttribute('tabindex');
      var template = document.createElement('template');
      (entry.media || []).forEach(function (media) {
        var slide = document.createElement('a');
        slide.href = leadClone.getAttribute('href');
        slide.setAttribute('aria-label', fresh.getAttribute('aria-label'));
        slide.appendChild(buildImage(media, { className: 'mc-collection-images__image', sizes: sizes, alt: label }));
        template.content.appendChild(slide);
      });
      fresh.append(leadClone, template);
      gallery.replaceWith(fresh);
    });
  }

  // AW26 cards: the swatch is a link. Capture phase, so the PDP navigation and its loading state do not start.
  document.addEventListener('click', function (e) {
    var swatch = e.target.closest && e.target.closest('.aw26-card-swatches a.color-swatch[data-variant-id]');
    if (!swatch || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var row = swatch.closest('.aw26-card-swatches');
    var data = rowData(row);
    var entry = data && data[swatch.getAttribute('data-variant-id')];
    if (!entry) return;
    e.preventDefault();
    if (swatch.classList.contains('is-selected')) return;
    row.querySelectorAll('.color-swatch.is-selected').forEach(function (el) { el.classList.remove('is-selected'); });
    swatch.classList.add('is-selected');
    applySwatch(row, entry);
  }, true);

  // Theme cards: the swatch is a radio. Handled here instead of the theme's product-card script, which loads the
  // product first and fades for half a second; the radio itself still checks as normal.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio') return;
    var row = input.closest('.product-card__swatch-list');
    if (!row || row.classList.contains('aw26-card-swatches')) return;
    var label = row.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]');
    var data = rowData(row);
    var entry = data && label && data[label.getAttribute('data-variant-id')];
    if (!entry) return;
    e.stopImmediatePropagation();
    applySwatch(row, entry);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observeRows(); });
  } else {
    observeRows();
  }
})();
